import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, X, Check, Lock } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface Props {
  stores: { id: string; name: string }[];
  role?: 'guard' | 'admin';
  /**
   * v0.4.9.2.4c4: the GLOBAL role (users.role) to assign. Needed for
   * TAdmin / SAdmin, which are employee roles with no per-store
   * equivalent in the old 'guard' | 'admin' union. Omit for the
   * original client-side behaviour.
   */
  globalRole?: string;
  mode: 'add' | 'remove';
  onClose: () => void;
  onSuccess: () => void;
}

export default function UserAddToStoresModal({ stores, role, mode, globalRole, onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set());
  const [storeMemberships, setStoreMemberships] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const [userTrainingEnabled, setUserTrainingEnabled] = useState(false);
  

    // Fetch user's store memberships when a user is selected
    useEffect(() => {
    if (!selectedUserId) {
        setStoreMemberships(new Set());
        return;
    }
    const fetchMemberships = async () => {
        const memberOf = new Set<string>();
        for (const store of stores) {
        try {
            const storeUsers = await api.getStoreUsers(store.id);
            if (storeUsers.some((u: any) => u.id === selectedUserId)) {
            memberOf.add(store.id);
            }
        } catch (err) {
            // store might not be accessible
        }
        }
        setStoreMemberships(memberOf);
    };
    fetchMemberships();
    }, [selectedUserId, stores]);

  // Search users when typing
  useEffect(() => {
    if (search.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const clientId = user?.clientId || undefined;
        // v0.4.9.2.4c4: the endpoint returns a WRAPPED envelope
        // { users: [...] }. This stored the whole object, so
        // `results.length` was undefined, the `> 0` guard was false,
        // and the result list never rendered — the search looked
        // completely dead. Unwrap here (or use api.searchUsers, which
        // does it for us).
        const data = await api.searchUsers(search);
        setResults(data);
      } catch (err) {
        console.error(err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, user?.clientId]);

  const handleToggleStore = (storeId: string) => {
    setSelectedStores(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const handleAction = async () => {
    if (!selectedUserId || selectedStores.size === 0) return;
    setLoading(true);
    setError('');
    try {
      for (const storeId of selectedStores) {
        if (mode === 'add') {
          // v0.4.9.2.4c4: pass the global role through. TAdmin and
          // SAdmin need role_in_store='admin' (they operate stores) while
          // carrying the elevated GLOBAL role on the user row.
          const storeRole = globalRole && globalRole !== 'guard' ? 'admin' : (role || 'guard');
          await api.addStoreUser(storeId, selectedUserEmail, storeRole, undefined, undefined, globalRole);
        } else {
          await api.removeStoreUser(storeId, selectedUserId);
        }
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {mode === 'add' ? `Add ${role} to Stores` : 'Remove from Stores'}
          </h2>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>

        {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

        {/* Search */}
        <div className="mb-4">
          <label className="text-sm font-medium">{t('admin.searchUser')}</label>
          <div className="relative mt-1">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
            placeholder={t('admin.searchUser')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            />
          </div>
        </div>
            {results.length > 0 && (
            <div className="border rounded divide-y mt-1 max-h-40 overflow-y-auto">
            {results.map(u => {
            const isRoleConflict = mode === 'add' && (
                (role === 'admin' && u.role !== 'admin') || 
                (role === 'guard' && u.role !== 'guard')
            );

            return (
                <div
                key={u.id}
                className={`p-2 text-sm cursor-pointer hover:bg-accent ${
                    selectedUserId === u.id ? 'bg-primary/10' : ''
                } ${isRoleConflict ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => {
                    if (isRoleConflict) return;
                    setSelectedUserId(u.id);
                    setSelectedUserEmail(u.email);
                    setSelectedStores(new Set());
                }}
                >
                <p className="font-medium">{u.name || u.email.split('@')[0]}</p>
                <p className="text-xs text-muted-foreground">{u.email} · {u.role}</p>
                </div>
            );
            })}
            </div>
            )}

        {/* Store selection */}
        {selectedUserId && (
          <div className="mb-4">
            <label className="text-sm font-medium">Select Stores</label>
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button
                size="sm"
                variant={selectedStores.size === stores.length ? 'default' : 'outline'}
                onClick={() => {
                  if (selectedStores.size === stores.length) {
                    setSelectedStores(new Set());
                  } else {
                    // Select all eligible stores
                    const eligible = stores.filter(s => {
                      const isMember = storeMemberships.has(s.id);
                      if (mode === 'add') return !isMember;
                      return isMember;
                    });
                    setSelectedStores(new Set(eligible.map(s => s.id)));
                  }
                }}
              >
                All {mode === 'add' ? 'Available' : 'Stores'}
              </Button>
              {stores.map(s => {
                const isMember = storeMemberships.has(s.id);
                const disabled = mode === 'add' ? isMember : !isMember;
                const selected = selectedStores.has(s.id);
                
                return (
                  <Button
                    key={s.id}
                    size="sm"
                    variant={selected ? 'default' : 'outline'}
                    onClick={() => !disabled && handleToggleStore(s.id)}
                    disabled={disabled}
                    className={disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    {disabled && <Lock className="w-3 h-3 mr-1" />}
                    {selected && !disabled && <Check className="w-3 h-3 mr-1" />}
                    {s.name}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleAction}
            disabled={!selectedUserId || selectedStores.size === 0 || loading}
          >
            {loading ? 'Processing...' : mode === 'add' ? `Add as ${role || 'User'}` : 'Remove from Stores'}
          </Button>
        </div>
      </div>
    </div>
  );
}